import { useRef, useEffect, useState } from 'react';
import { C } from '../../lib/tokens.js';
import { t } from '../../lib/data.js';
import { useLookups } from '../../lib/LookupsContext.jsx';
import { useSystemNotifications } from '../../lib/useSystemNotifications.js';
import { useViewport } from '../../lib/useViewport.js';
import { useClickOutside } from '../../lib/useClickOutside.js';
import { relTime, dateTimeFull } from '../../lib/utils.js';
import { Button, IconButton } from '../ui/Button.jsx';
import { Avatar } from '../ui/Avatar.jsx';

function hit(str, q) { return (str || '').toLowerCase().includes(q); }

function searchAll(raw, searchData) {
  const q = raw.toLowerCase();
  const requests = searchData?.requests ?? [];
  const orders = searchData?.orders ?? [];
  const invoices = searchData?.invoices ?? [];
  const payments = searchData?.payments ?? [];
  return {
    requests: requests.filter(r =>
      hit(r.summary, q) || hit(r.id, q) || hit(r.docNumber, q) || hit(r.module, q)
    ).slice(0, 5),
    orders:   orders.filter(r => hit(r.no, q) || hit(r.customer, q) || hit(r.status, q)).slice(0, 3),
    invoices: invoices.filter(r => hit(r.no, q) || hit(r.desc, q) || hit(r.kind, q)).slice(0, 3),
    payments: payments.filter(r => hit(r.ref, q) || hit(r.allocated, q) || hit(r.method, q)).slice(0, 3),
  };
}

function fmt(n) {
  return new Intl.NumberFormat('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

export function Header({ user, role, dark, onNew, onHelp, onLogout, onSettings, searchQuery = '', onSearch, onResultSelect, searchData, onMenuClick }) {
  const inputRef      = useRef(null);
  const containerRef  = useRef(null);
  const userMenuRef   = useRef(null);
  const notifRef      = useRef(null);
  const [open, setOpen]         = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const { notifications, unreadCount, markRead } = useSystemNotifications();
  const { isMobile } = useViewport();

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
      if (e.key === 'Escape' && document.activeElement === inputRef.current) {
        onSearch?.('');
        setOpen(false);
        inputRef.current.blur();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onSearch]);

  useClickOutside(containerRef, true, () => setOpen(false));
  useClickOutside(userMenuRef, true, () => setUserMenu(false));
  useClickOutside(notifRef, true, () => setNotifOpen(false));

  const select = (kind, id, docNumber) => {
    onResultSelect?.({ kind, id, docNumber });
    onSearch?.('');
    setOpen(false);
    inputRef.current?.blur();
  };

  const results = searchQuery ? searchAll(searchQuery, searchData) : null;
  const hasAny  = results && (results.requests.length || results.orders.length || results.invoices.length || results.payments.length);

  const bg     = dark ? C.deepsea : C.tin;
  const fg     = dark ? C.tin : C.fg;
  const fg2    = dark ? C.foam : C.iron;
  const border = dark ? C.abyss : C.mercury;

  const closeMobileSearch = () => { setMobileSearchOpen(false); setOpen(false); onSearch?.(''); };

  // ── Search takeover: on mobile the 320px-wide search box + anchored dropdown
  // doesn't fit - it becomes a full-width row replacing the rest of the header
  // (logo/menu/notifications/user) instead, closer to a native app's search UX.
  if (isMobile && mobileSearchOpen) {
    return (
      <header style={{
        height: 52, background: bg, borderBottom: `1px solid ${border}`,
        display: 'flex', alignItems: 'center', padding: '0 10px', gap: 8, flex: 'none',
        color: fg, position: 'sticky', top: 0, zIndex: 50,
      }}>
        <IconButton icon="arrow_back" color={fg2} hoverBg={dark ? C.abyss : C.platinum} onClick={closeMobileSearch} />
        <div ref={containerRef} style={{ position: 'relative', flex: 1 }}>
          <div style={{
            display: 'flex', alignItems: 'center',
            background: dark ? C.abyss : C.platinum,
            border: `1px solid ${open && searchQuery ? C.liquid : (dark ? C.tide : C.mercury)}`,
            borderRadius: open && searchQuery ? '4px 4px 0 0' : 4,
            padding: '6px 10px', transition: 'border-color 120ms',
          }}>
            <span className="material-icons" style={{ fontSize: 16, color: fg2, marginRight: 6 }}>search</span>
            <input
              ref={inputRef}
              name="global-search"
              autoComplete="off"
              autoFocus
              value={searchQuery}
              onChange={e => { onSearch?.(e.target.value); setOpen(true); }}
              onFocus={() => { if (searchQuery) setOpen(true); }}
              placeholder={t('app.search')}
              style={{ background: 'transparent', border: 'none', outline: 'none', fontSize: 12, flex: 1, fontFamily: 'Roboto', color: fg }}
            />
            {searchQuery && (
              <button onClick={() => { onSearch?.(''); setOpen(false); inputRef.current?.focus(); }} type="button"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', color: fg2, fontSize: 16, lineHeight: 1, fontFamily: 'Roboto' }}>
                ×
              </button>
            )}
          </div>
          {open && searchQuery && (
            <SearchResultsDropdown hasAny={hasAny} results={results} searchQuery={searchQuery} select={select} />
          )}
        </div>
      </header>
    );
  }

  return (
    <header style={{
      height: 52, background: bg, borderBottom: `1px solid ${border}`,
      display: 'flex', alignItems: 'center', padding: '0 16px', gap: 14, flex: 'none',
      color: fg, position: 'sticky', top: 0, zIndex: 50,
    }}>
      {isMobile && (
        <IconButton icon="menu" color={fg2} hoverBg={dark ? C.abyss : C.platinum} onClick={onMenuClick} />
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        {dark ? (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
            <span style={{ fontFamily: 'Roboto', fontWeight: 300, fontSize: 18, color: C.tin, letterSpacing: '-0.01em' }}>act</span>
            <span style={{ width: 4, height: 4, borderRadius: '50%', background: C.liquid, transform: 'translateY(-2px)' }} />
            <span style={{ fontFamily: 'Roboto', fontWeight: 500, fontSize: 18, color: C.tin, letterSpacing: '-0.01em' }}>fact</span>
          </div>
        ) : (
          <img src="assets/logo.svg" alt="ActFact" style={{ height: 22 }} />
        )}
        {!isMobile && (
          <div style={{
            fontSize: 11, fontWeight: 500, color: dark ? C.sky : C.liquid,
            textTransform: 'uppercase', letterSpacing: '0.08em', paddingLeft: 8,
            borderLeft: `1px solid ${dark ? C.tide : C.mercury}`,
          }}>{t('app.brand')}</div>
        )}
      </div>

      <div style={{ flex: 1 }} />

      {/* ── Search box + dropdown (icon-only on mobile, opens the takeover above) ── */}
      {isMobile ? (
        <IconButton icon="search" color={fg2} hoverBg={dark ? C.abyss : C.platinum} onClick={() => setMobileSearchOpen(true)} />
      ) : (
        <div ref={containerRef} style={{ position: 'relative' }}>
          <div style={{
            display: 'flex', alignItems: 'center',
            background: dark ? C.abyss : C.platinum,
            border: `1px solid ${open && searchQuery ? C.liquid : (dark ? C.tide : C.mercury)}`,
            borderRadius: open && searchQuery ? '4px 4px 0 0' : 4,
            padding: '6px 10px', flex: '0 1 320px', minWidth: 160, transition: 'border-color 120ms',
          }}>
            <span className="material-icons" style={{ fontSize: 16, color: fg2, marginRight: 6 }}>search</span>
            <input
              ref={inputRef}
              name="global-search"
              autoComplete="off"
              value={searchQuery}
              onChange={e => { onSearch?.(e.target.value); setOpen(true); }}
              onFocus={() => { if (searchQuery) setOpen(true); }}
              placeholder={t('app.search')}
              style={{ background: 'transparent', border: 'none', outline: 'none', fontSize: 12, flex: 1, fontFamily: 'Roboto', color: fg }}
            />
            {searchQuery ? (
              <button onClick={() => { onSearch?.(''); setOpen(false); inputRef.current?.focus(); }} type="button"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', color: fg2, fontSize: 16, lineHeight: 1, fontFamily: 'Roboto' }}>
                ×
              </button>
            ) : (
              <kbd style={{ fontSize: 10, color: fg2, fontFamily: 'Roboto Mono', border: `1px solid ${dark ? C.tide : C.mercury}`, padding: '1px 5px', borderRadius: 3 }}>{t('app.kbd_search')}</kbd>
            )}
          </div>

          {open && searchQuery && (
            <SearchResultsDropdown hasAny={hasAny} results={results} searchQuery={searchQuery} select={select} />
          )}
        </div>
      )}

      <div ref={notifRef} style={{ position: 'relative', flexShrink: 0 }}>
        <div style={{ position: 'relative' }}>
          <IconButton icon={unreadCount > 0 ? 'notifications' : 'notifications_none'} color={fg2}
            hoverBg={dark ? C.abyss : C.platinum} active={notifOpen} onClick={() => setNotifOpen(v => !v)} />
          {unreadCount > 0 && (
            <span style={{
              position: 'absolute', top: 2, right: 2, minWidth: 14, height: 14, padding: '0 3px',
              borderRadius: 999, background: C.rust, color: C.tin, fontSize: 9, fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Roboto',
              pointerEvents: 'none',
            }}>{unreadCount > 99 ? '99+' : unreadCount}</span>
          )}
        </div>

        {notifOpen && (
          <div style={isMobile ? {
            // absolute+right:0 anchors to this icon's own position, not the screen
            // edge - on mobile the help icon + avatar sit to its right, so a
            // near-full-width panel anchored that way pushed off-screen left.
            // Anchor to the viewport instead.
            position: 'fixed', top: 52, left: 10, right: 10,
            background: C.tin, border: `1px solid ${C.mercury}`, borderRadius: 6,
            boxShadow: '0 6px 20px rgba(34,52,105,0.12)', zIndex: 200,
            maxHeight: '70vh', overflowY: 'auto',
          } : {
            position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: 340,
            background: C.tin, border: `1px solid ${C.mercury}`, borderRadius: 6,
            boxShadow: '0 6px 20px rgba(34,52,105,0.12)', zIndex: 200,
            maxHeight: 420, overflowY: 'auto',
          }}>
            <div style={{ padding: '10px 14px', borderBottom: `1px solid ${C.mercury}`, fontSize: 12, fontWeight: 600, color: C.deepsea, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {t('app.notifications')}
            </div>
            {notifications.length === 0 ? (
              <div style={{ padding: '18px 14px', fontSize: 12, color: C.iron, textAlign: 'center' }}>{t('notif.empty')}</div>
            ) : notifications.map(n => (
              <div key={n.id} onClick={() => markRead(n.id)}
                style={{
                  padding: '10px 14px', borderBottom: `1px solid ${C.mercury}`, cursor: 'pointer',
                  background: n.read ? 'transparent' : C.rowActive,
                }}>
                <div style={{ fontSize: 12, fontWeight: n.read ? 400 : 700, color: C.fg }}>{n.title}</div>
                {n.message && (
                  <div style={{ fontSize: 11, color: C.iron, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    {n.message}
                  </div>
                )}
                <div style={{ fontSize: 10, color: C.silver, marginTop: 4 }} title={dateTimeFull(n.created)}>{relTime(n.created)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      <IconButton icon="help_outline" color={fg2} hoverBg={dark ? C.abyss : C.platinum} onClick={onHelp} />

      <div ref={userMenuRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 8, borderLeft: `1px solid ${border}`, cursor: 'pointer', flexShrink: 0 }}
        onClick={() => setUserMenu(v => !v)}>
        <Avatar user={user} size={30} />
        {!isMobile && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, maxWidth: 140, overflow: 'hidden' }}>
              <span style={{ fontSize: 12, fontWeight: 500, color: fg, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.name}</span>
              <span style={{ fontSize: 10, color: fg2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.company || (role === 'agent' ? 'ActFact Support' : '')}</span>
            </div>
            <span className="material-icons" style={{ fontSize: 14, color: fg2, marginLeft: 2 }}>
              {userMenu ? 'expand_less' : 'expand_more'}
            </span>
          </>
        )}

        {userMenu && (
          <div style={{
            position: 'absolute', top: 'calc(100% + 8px)', right: 0,
            background: C.tin, border: `1px solid ${C.mercury}`,
            borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            minWidth: 160, zIndex: 200, overflow: 'hidden',
          }}>
            <UserMenuItem icon="person_outline" label={user.name} sub={user.email} disabled />
            <div style={{ height: 1, background: C.mercury, margin: '4px 0' }} />
            <UserMenuItem icon="settings" label={t('app.settings')} onClick={() => { setUserMenu(false); onSettings?.(); }} />
            <UserMenuItem icon="logout" label={t('app.logout')} onClick={onLogout} />
          </div>
        )}
      </div>
    </header>
  );
}

function UserMenuItem({ icon, label, sub, onClick, disabled }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => !disabled && setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px',
        cursor: disabled ? 'default' : 'pointer',
        background: hov ? C.platinum : 'transparent',
      }}
    >
      <span className="material-icons" style={{ fontSize: 16, color: C.iron }}>{icon}</span>
      <div>
        <div style={{ fontSize: 12, fontWeight: 500, color: C.fg }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: C.iron }}>{sub}</div>}
      </div>
    </div>
  );
}

// ─── Help panel ───────────────────────────────────────────────────────────────

export function HelpPanel({ onClose }) {
  const { requestTypes } = useLookups();
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <>
      <div onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.15)', zIndex: 150 }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, width: 'min(360px, 100vw)', height: '100vh',
        background: C.tin, borderLeft: `1px solid ${C.mercury}`,
        boxShadow: '-4px 0 20px rgba(0,0,0,0.10)', zIndex: 151,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${C.mercury}`, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="material-icons" style={{ fontSize: 18, color: C.iron }}>help_outline</span>
          <span style={{ flex: 1, fontSize: 14, fontWeight: 500, color: C.deepsea }}>{t('help.title')}</span>
          <IconButton icon="close" title={t('help.close')} onClick={onClose} />
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 24 }}>
          <HelpSection title={t('help.getting_started')}>
            <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[
                t('help.step1'),
                t('help.step2'),
                t('help.step3'),
                t('help.step4'),
              ].map((item, i) => <li key={i} style={{ fontSize: 13, color: C.fg, lineHeight: 1.5 }}>{item}</li>)}
            </ul>
          </HelpSection>

          <HelpSection title={t('help.keyboard_shortcuts')}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <ShortcutRow keys="Ctrl+K">{t('help.shortcut.focus_search')}</ShortcutRow>
              <ShortcutRow keys="Escape">{t('help.shortcut.close_panel')}</ShortcutRow>
            </div>
          </HelpSection>

          <HelpSection title={t('help.contact_support')}>
            <p style={{ margin: 0, fontSize: 13, color: C.iron }}>
              {t('help.contact_prefix')}{' '}
              <a href="mailto:support@actfact.com" style={{ color: C.liquid }}>support@actfact.com</a>
            </p>
          </HelpSection>
        </div>
      </div>
    </>
  );
}

function HelpSection({ title, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 500, color: C.iron, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>{title}</div>
      {children}
    </div>
  );
}

function ShortcutRow({ keys, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12 }}>
      <kbd style={{ fontFamily: 'Roboto Mono', fontSize: 11, color: C.fg, background: C.platinum, border: `1px solid ${C.mercury}`, padding: '2px 6px', borderRadius: 3, flex: 'none' }}>{keys}</kbd>
      <span style={{ color: C.iron }}>{children}</span>
    </div>
  );
}

// ─── Search dropdown ──────────────────────────────────────────────────────────

// Shared between the desktop anchored dropdown and the mobile search takeover -
// same result list, just positioned differently by each caller.
function SearchResultsDropdown({ hasAny, results, searchQuery, select }) {
  return (
    <div style={{
      position: 'absolute', top: '100%', left: 0, right: 0,
      background: C.tin, border: `1px solid ${C.liquid}`, borderTop: 'none',
      borderRadius: '0 0 6px 6px',
      boxShadow: '0 6px 20px rgba(34,52,105,0.12)', zIndex: 200,
      maxHeight: 480, overflowY: 'auto',
    }}>
      {!hasAny ? (
        <div style={{ padding: '14px 16px', fontSize: 12, color: C.iron, textAlign: 'center' }}>
          No results for <strong>"{searchQuery}"</strong>
        </div>
      ) : (
        <>
          {results.requests.length > 0 && (
            <DropSection label="Requests" icon="forum">
              {results.requests.map(r => (
                <DropRow key={r.id} onClick={() => select('request', r.id, r.docNumber)}>
                  <span style={{ fontFamily: 'Roboto Mono', fontSize: 11, color: C.liquid, flex: 'none' }}>#{r.docNumber ?? r.id}</span>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12, color: C.fg }}>{r.summary}</span>
                  <span style={{ fontSize: 11, color: C.iron, flex: 'none' }}>{r.module}</span>
                </DropRow>
              ))}
            </DropSection>
          )}
          {results.orders.length > 0 && (
            <DropSection label="Orders" icon="shopping_cart">
              {results.orders.map((r, i) => (
                <DropRow key={i} onClick={() => select('order', r.no)}>
                  <span style={{ fontFamily: 'Roboto Mono', fontSize: 11, color: C.iron, flex: 'none' }}>{r.no}</span>
                  <span style={{ flex: 1 }} />
                  <span style={{ fontSize: 11, color: C.iron, flex: 'none' }}>{r.status}</span>
                  <span style={{ fontFamily: 'Roboto Mono', fontSize: 11, color: C.fg, flex: 'none' }}>{r.currency} {fmt(r.total)}</span>
                </DropRow>
              ))}
            </DropSection>
          )}
          {results.invoices.length > 0 && (
            <DropSection label="Invoices" icon="receipt">
              {results.invoices.map((r, i) => (
                <DropRow key={i} onClick={() => select('invoice', r.no)}>
                  <span style={{ fontSize: 12, color: C.fg, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.no}</span>
                  <span style={{ fontSize: 11, color: C.iron, flex: 'none' }}>{r.date}</span>
                  <span style={{ fontFamily: 'Roboto Mono', fontSize: 11, color: C.fg, flex: 'none' }}>{r.currency} {fmt(r.total)}</span>
                </DropRow>
              ))}
            </DropSection>
          )}
          {results.payments.length > 0 && (
            <DropSection label="Payments" icon="payments">
              {results.payments.map((r, i) => (
                <DropRow key={i} onClick={() => select('payment', r.ref)}>
                  <span style={{ fontFamily: 'Roboto Mono', fontSize: 11, color: C.iron, flex: 'none' }}>{r.ref}</span>
                  <span style={{ fontSize: 11, color: C.iron, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>· {r.allocated}</span>
                  <span style={{ fontFamily: 'Roboto Mono', fontSize: 11, color: C.fg, flex: 'none' }}>EUR {fmt(r.amount)}</span>
                </DropRow>
              ))}
            </DropSection>
          )}
        </>
      )}
    </div>
  );
}

function DropSection({ label, icon, children }) {
  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '8px 14px 4px', fontSize: 10, fontWeight: 600,
        color: C.iron, textTransform: 'uppercase', letterSpacing: '0.06em',
        borderTop: `1px solid ${C.mercury}`,
      }}>
        <span className="material-icons" style={{ fontSize: 13 }}>{icon}</span>
        {label}
      </div>
      {children}
    </div>
  );
}

function DropRow({ onClick, children }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '7px 14px', cursor: 'pointer',
        background: hov ? C.rowHover : 'transparent',
      }}
    >
      {children}
    </div>
  );
}
